import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { authService } from '../services/auth';
import type { AuthenticatedUser } from '../services/api';

interface AuthContextType {
  user: AuthenticatedUser | null;
  isLoggedIn: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; user?: AuthenticatedUser; error?: string }>;
  logout: () => void;
  checkAuthState: () => void;
  getLoginInfo: () => any;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = () => {
    try {
      const storedUser = authService.getStoredUser();
      const loginValid = authService.isLoginValid();

      if (storedUser && loginValid) {
        setUser(storedUser);
        setIsLoggedIn(true);
      } else {
        setUser(null);
        setIsLoggedIn(false);
        if (storedUser && !loginValid) {
          authService.logout();
        }
      }
    } catch (error) {
      console.error('Erro ao verificar estado de autenticação:', error);
      setUser(null);
      setIsLoggedIn(false);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (username: string, password: string) => {
    setIsLoading(true);
    try {
      const result = await authService.login(username, password);

      if (result.success && result.user) {
        setUser(result.user);
        setIsLoggedIn(true);
        return { success: true, user: result.user };
      } else {
        return { success: false, error: result.error || 'Erro no login' };
      }
    } catch (error: any) {
      return { success: false, error: error.message || 'Erro de conexão' };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setIsLoggedIn(false);
  };

  const getLoginInfo = () => {
    return authService.getLoginInfo();
  };

  const value = {
    user,
    isLoggedIn,
    isLoading,
    login,
    logout,
    checkAuthState,
    getLoginInfo,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

export default AuthProvider;
